import React, { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import queryString from "query-string";
import { useTranslation } from "react-i18next";
import { Box, CircularProgress } from "@mui/material";

import Button, { Color } from "../../../components/Form/Button";
import AgentService from "Services/agent.service";
import { AppPageCardInform } from "Components/AppPageCardInform";
import { EmojiSad as EmojiSadIcon } from "iconsax-react";
import { SignUpContainer } from "Components/SignUpContainer";
import { InviteContainer } from "Components/InviteContainer/InviteContainer";
import { MOVING_COMPANY_AGENT_SIGN_IN_SELECT } from "Constants";
import { useHistory } from "react-router";

export type ValidateShipeeziNetworkInviteTokenPayload = {
  token: string;
};

export type AcceptShipeeziNetworkInvitePayload = {
  token: string;
  password: string;
};

export type AcceptAndAssociateShipeeziNetworkInvitePayload = {
  token: string;
  selectedCompanyId: number;
};

export type AcceptIndependentShipeeziNetworkInvitePayload = {
  token: string;
};

export const AcceptShipeeziNetworkInvite = () => {
  const { t: translate } = useTranslation();
  const history = useHistory();

  // eslint-disable-next-line no-restricted-globals
  const parsed: { token?: string } = queryString.parse(location.search);

  const [actionCompleted, setActionCompleted] = useState<boolean>(false);
  const [showSignUpCard, setShowSignUpCard] = useState<boolean>(false);
  const [agentRegistered, setAgentRegistered] = useState<boolean>(false);
  const [companyName, setCompanyName] = useState<string>();

  // Mutation para rejeitar o convite
  const validateMutation = useMutation({
    mutationFn: (payload: ValidateShipeeziNetworkInviteTokenPayload) =>
      AgentService.validateInvite(payload),
    onSuccess: ({ data }) => {
      setActionCompleted(true);
      setCompanyName(data.companyName);
    },
    onError: (error) => {
      console.error("Erro ao validar convite:", error);
    },
  });

  const acceptInviteMutation = useMutation({
    mutationFn: (payload: AcceptShipeeziNetworkInvitePayload) =>
      AgentService.acceptInvite(payload),
    onSuccess: ({ data }) => {
      setAgentRegistered(true)
    },
    onError: (error) => {
      console.error("Erro ao validar convite:", error);
    },
  });

  // Função para executar a rejeição automaticamente ao carregar a página
  useEffect(() => {
    if (parsed?.token && !actionCompleted) {
      validate();
    }
  }, [parsed?.token, actionCompleted]);

  const validate = () => {
    if (!parsed?.token) return;

    validateMutation.mutate({
      token: parsed.token,
    });
  };

  const handleCloseWindow = () => {
    window.close();
  };

  const handleSignToSelectCompanyClick = () => {
    if (!parsed?.token) return;
    localStorage.setItem(MOVING_COMPANY_AGENT_SIGN_IN_SELECT, parsed.token);
    history.replace("/");
  }

  if (validateMutation.isLoading) {
    return <AppPageCardInform icon={<CircularProgress />} title={translate("loading")} />;
  }

  if (validateMutation.isError) {
    const error = validateMutation.error as any;
    let errorMessage = translate(
      "anErrorOccurredWhileProcessingYourRequestPleaseTryAgainOrContactSupport"
    );
    let errorTitle = translate("errorProcessingRequest");

    if (error?.response?.status === 400 || error?.response?.status === 401) {
      errorTitle = translate("invalidOrExpiredLink");
      errorMessage = translate(
        "thisInvitationLinkIsInvalidOrHasAlreadyExpiredPleaseContactUsToReceiveANewInvitation"
      );
    }

    return (
      <AppPageCardInform
        icon={<EmojiSadIcon size={32} />}
        title={errorTitle}
        content={errorMessage}
        actionText={translate("closeWindow")}
        onActionClick={handleCloseWindow}
        actionColor={Color.SECONDARY_DARK}
      />
    );
  }

  if (!parsed?.token) {
    return (
      <AppPageCardInform
        icon={<EmojiSadIcon size={32} />}
        title={translate("invalidLink")}
        content={translate("thisLinkIsNotValidPleaseCheckIfYouHaveAccessedTheCorrectLink")}
        actionText={translate("closeWindow")}
        onActionClick={handleCloseWindow}
        actionColor={Color.SECONDARY_DARK}
      />
    );
  }

  // Estado de sucesso
  if (actionCompleted) {
    if (showSignUpCard) {
      return (
        <SignUpContainer
          showCompletedPage={agentRegistered}
          loading={acceptInviteMutation.isLoading}
          handleSubmitPassword={(password) => {
            acceptInviteMutation.mutate({ token: parsed?.token!, password });
          }}
        />
      );
    }

    return (
      <InviteContainer
        title={"You’re ready to join [company_name]’s Shipeezi Network".replace(
          "[company_name]",
          companyName!
        )}
        description={translate("areYouAnExistingShipeeziUser")}
        actions={
          <Box display="flex" gap="20px" justifyContent="center" alignItems="center" width="100%" paddingTop="24px">
            <Button data-cy="go-to-login-page-button" customColor={Color.SUCCESS} fullWidth onClick={handleSignToSelectCompanyClick}>
              {translate("yesGoToLoginPage")}
            </Button>
            <Button 
              data-cy="go-to-login-page-button" 
              customColor={Color.GRAY_600} 
              fullWidth 
              onClick={() => setShowSignUpCard(true)}
            >
              {translate("noRegisterAsAnAgent")}
            </Button>
          </Box>
        }
      />
    );
  }

  return <AppPageCardInform icon={<CircularProgress />} title={translate("loading")} />;
};
